import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../components/mobileUi";
import {
  ActionButton,
  ActionPopover,
  ALL_VALUE,
  CenterSpinner,
  DateFilterField,
  EmptyText,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  RowKV,
  SelectSheet,
  StatusChip,
  SummaryCard,
  useSheet,
} from "../../components/managementUi";
import {
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { useEmployeeAuth } from "../../auth/EmployeeAuthContext";
import {
  EMP_DEPARTMENT,
  JOB_TITLE,
  filterJobPosition,
  useSupportBusinessEndpoint,
} from "../../utils/managerRole";
import { ALL_OPTION, buildOptionSheetData, useFilterOptions, validId } from "../../utils/useFilterOptions";
import {
  approveLeaveApi,
  getBusinessLeaveListApi,
  getSupportBusinessLeaveListApi,
} from "../../api/leaveManager.api";
import { EMP_BASE_PATH, REQUEST_STATUS } from "../../config";

const LIMIT = 10;
const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const STATUS_OPTIONS = [
  { key: ALL_VALUE, label: "Tất cả" },
  { key: REQUEST_STATUS.PENDING, label: "Đang chờ duyệt" },
  { key: REQUEST_STATUS.APPROVED, label: "Đã duyệt" },
  { key: REQUEST_STATUS.REJECTED, label: "Từ chối" },
];
const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ", sortBy: "createdAt", sortOrder: "desc" },
  { key: "OLDEST", label: "Cũ đến mới", sortBy: "createdAt", sortOrder: "asc" },
];

export const leaveStatusUi = (status) => {
  if (status === REQUEST_STATUS.APPROVED) return { label: "ĐÃ DUYỆT", bg: "#E9FFF4", color: "#0BA859" };
  if (status === REQUEST_STATUS.REJECTED) return { label: "TỪ CHỐI", bg: "#FFF1F0", color: "#E5484D" };
  return { label: "ĐANG CHỜ DUYỆT", bg: "#FFF7E8", color: "#F79D0D" };
};

const LeaveCard = ({ item, onClick, onOpenMenu }) => {
  const ui = leaveStatusUi(item?.status);
  const emp = item?.employeeObject;
  return (
    <Box
      role="button"
      tabIndex={0}
      w="100%"
      textAlign="left"
      cursor="pointer"
      onClick={onClick}
      bg="white"
      border="1px solid"
      borderColor={M.border}
      borderRadius="12px"
      p="14px"
    >
      <Flex w="100%" align="center" justify="space-between" gap="8px">
        <Box minW="0">
          <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
            {emp?.fullName || "-"} - {emp?.code || "-"}
          </Text>
          <Box h="8px" />
          <Flex align="center" gap="8px" flexWrap="wrap">
            <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
              {fmtDate(item?.createdAt)}
            </Text>
            <StatusChip label={ui.label} bg={ui.bg} color={ui.color} />
          </Flex>
        </Box>
        {item?.status === REQUEST_STATUS.PENDING ? <ActionButton onOpen={(rect) => onOpenMenu(item, rect)} /> : null}
      </Flex>

      <Box h="1px" bg={M.border} mt="12px" mb="12px" />

      <Flex direction="column" gap="8px">
        <RowKV label="Ngày yêu cầu" value={fmtDate(item?.requestDate)} />
        <RowKV label="Ngày bắt đầu" value={fmtDate(item?.startDate)} />
        <RowKV label="Số ngày nghỉ" value={`${item?.daysRequested || 0}`} />
        <RowKV label="Loại nghỉ phép" value={item?.leaveType?.name} />
        <RowKV label="Lý do" value={item?.reason || "-"} />
        {emp?.department === EMP_DEPARTMENT.BUSINESS && (
          <>
            <RowKV label="Khối" value={emp?.saleBlock?.name || "-"} />
            <RowKV label="Vùng" value={emp?.saleArea?.name || "-"} />
          </>
        )}
        {emp?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS && (
          <RowKV label="Phòng" value={emp?.supportDepartment?.name || "-"} />
        )}
      </Flex>
    </Box>
  );
};

const LeaveApprovalList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const toast = useToast();
  const { user } = useEmployeeAuth();
  const fetchApi = useSupportBusinessEndpoint(user) ? getSupportBusinessLeaveListApi : getBusinessLeaveListApi;
  const jobPosition = filterJobPosition(user);

  const [filterOpen, setFilterOpen] = useState(true);
  const [status, setStatus] = useState(REQUEST_STATUS.PENDING);
  const [area, setArea] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [supportDept, setSupportDept] = useState(ALL_OPTION);
  const [branch, setBranch] = useState(ALL_OPTION);
  const [fromDate, setFromDate] = useState(moment().startOf("year").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const [menu, setMenu] = useState(null); // { item, rect }

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(
    () => (sheet.type === "BLOCK" ? { saleAreaId: validId(area) } : undefined),
    [sheet.type, area]
  );
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const sortOption = SORT_OPTIONS.find((s) => s.key === sortKey) || SORT_OPTIONS[0];

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      requestDateFrom: fromDate ? moment(fromDate).format("YYYY-MM-DD") : undefined,
      requestDateTo: toDate ? moment(toDate).format("YYYY-MM-DD") : undefined,
      status: status !== ALL_VALUE ? status : undefined,
      sortBy: sortOption.sortBy,
      sortOrder: sortOption.sortOrder,
      saleAreaId: validId(area),
      saleBlockId: validId(block),
      supportDepartmentId: validId(supportDept),
      branchId: validId(branch),
    }),
    [fromDate, toDate, status, sortOption, area, block, supportDept, branch]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await fetchApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setTotal(data?.pagination?.total ?? list.length);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [fetchApi, buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, fromDate, toDate, sortKey, area?._id, block?._id, supportDept?._id, branch?._id]);

  const approve = useCallback(
    async (item, nextStatus) => {
      try {
        await approveLeaveApi(item._id, nextStatus);
        toast({ title: "Duyệt Thành công", status: "success", duration: 2500, position: "top" });
        fetchPage(1, "replace");
      } catch (e) {
        toast({ title: e?.message || "Duyệt thất bại", status: "error", duration: 3000, position: "top" });
      }
    },
    [toast, fetchPage]
  );

  const statusLabel = STATUS_OPTIONS.find((s) => s.key === status)?.label ?? "Tất cả";

  // Lưới bộ lọc theo chức vụ - khớp app.
  const filterFields = useMemo(() => {
    const f = {
      status: <FilterField key="status" label="Trạng thái" value={statusLabel} onClick={() => sheet.open("STATUS")} />,
      area: <FilterField key="area" label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />,
      block: <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
      sdept: (
        <FilterField
          key="sdept"
          label="Phòng ban"
          value={supportDept?.name || "Tất cả"}
          onClick={() => sheet.open("SUPPORT_DEPARTMENT")}
        />
      ),
      branch: (
        <FilterField key="branch" label="Chi nhánh" value={branch?.name || "Tất cả"} onClick={() => sheet.open("BRANCH")} />
      ),
      from: <DateFilterField key="from" label="Từ ngày" value={fromDate} onChange={setFromDate} />,
      to: <DateFilterField key="to" label="Đến ngày" value={toDate} onChange={setToDate} />,
    };
    switch (jobPosition) {
      case JOB_TITLE.TRUONG_KHOI:
        return [f.status, f.from, f.to];
      case JOB_TITLE.TRUONG_VUNG:
        return [f.status, f.block, f.from, f.to];
      case JOB_TITLE.TRUONG_PHONG:
        return [f.status, f.sdept, f.from, f.to];
      case JOB_TITLE.TGD:
        return [f.status, f.area, f.block, f.from, f.to];
      default:
        return [f.status, f.area, f.block, f.from, f.to];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, statusLabel, area, block, supportDept, branch, fromDate, toDate]);

  const sheetTitle =
    { STATUS: "Trạng thái", SORT: "Sắp xếp", AREA: "Vùng", BLOCK: "Khối", SUPPORT_DEPARTMENT: "Phòng ban", BRANCH: "Chi nhánh" }[
      sheet.type
    ] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "STATUS") return STATUS_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT") return SORT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "SUPPORT_DEPARTMENT") return buildOptionSheetData(options.items, supportDept);
    if (sheet.type === "BRANCH") return buildOptionSheetData(options.items, branch);
    return [];
  }, [sheet.type, options.items, area, block, supportDept, branch]);

  const selectedKey = {
    STATUS: status,
    SORT: sortKey,
    AREA: area?._id,
    BLOCK: block?._id,
    SUPPORT_DEPARTMENT: supportDept?._id,
    BRANCH: branch?._id,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "STATUS") setStatus(raw);
    if (sheet.type === "SORT") setSortKey(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "SUPPORT_DEPARTMENT") setSupportDept(raw);
    if (sheet.type === "BRANCH") setBranch(raw);
    sheet.close();
    setSheetSearch("");
  };

  const popovers = (
    <>
      <ActionPopover
        open={!!menu}
        anchor={menu?.rect}
        onClose={() => setMenu(null)}
        items={[
          {
            key: "approve",
            label: "Phê duyệt",
            icon: appImages.icon_check_circle,
            onPress: () => approve(menu.item, REQUEST_STATUS.APPROVED),
          },
          {
            key: "reject",
            label: "Từ chối",
            icon: appImages.icon_reject_circle,
            danger: true,
            onPress: () => approve(menu.item, REQUEST_STATUS.REJECTED),
          },
        ]}
      />

      <SelectSheet
        isOpen={sheet.isOpen}
        onClose={() => {
          sheet.close();
          setSheetSearch("");
        }}
        title={sheetTitle}
        data={sheetData}
        selectedKey={selectedKey}
        onSelect={onPick}
        searchable={!["STATUS", "SORT"].includes(sheet.type)}
        searchValue={sheetSearch}
        onChangeSearch={setSheetSearch}
        loading={options.loading}
        loadingMore={options.loadingMore}
        hasNextPage={options.hasMore}
        onEndReached={options.loadMore}
      />
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  // Cùng bộ lọc / cùng API / cùng hành động duyệt, chỉ hiển thị dạng bảng.
  if (isDesktop) {
    const rows = items.map((it) => {
      const emp = it?.employeeObject;
      const ui = leaveStatusUi(it?.status);
      return {
        id: it._id,
        raw: it,
        ui,
        employee: `${emp?.fullName || "-"} - ${emp?.code || "-"}`,
        unit:
          emp?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS
            ? emp?.supportDepartment?.name || "-"
            : [emp?.saleBlock?.name, emp?.saleArea?.name].filter(Boolean).join(" / ") || "-",
        createdAt: fmtDate(it?.createdAt),
        requestDate: fmtDate(it?.requestDate),
        startDate: fmtDate(it?.startDate),
        days: `${it?.daysRequested || 0}`,
        leaveType: it?.leaveType?.name || "-",
        reason: it?.reason || "-",
      };
    });

    return (
      <DesktopPage title="Phê duyệt đơn xin nghỉ phép">
        <DesktopStatRow items={[{ key: "total", title: "Tổng số đơn", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el, i) =>
              React.cloneElement(el, { key: el.key || i, width: "220px" }),
            )}
            <Box w="220px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có đơn xin nghỉ phép"
            onRowClick={(row) =>
              navigate(`${EMP_BASE_PATH}/quan-ly/phe-duyet-nghi-phep/${row.id}`)
            }
            rows={rows}
            headers={[
              { key: "employee", label: "Nhân viên", align: "left", width: "16%" },
              { key: "unit", label: "Khối / Vùng / Phòng", align: "left", width: "13%" },
              { key: "createdAt", label: "Ngày tạo", width: "8%" },
              { key: "requestDate", label: "Ngày yêu cầu", width: "9%" },
              { key: "startDate", label: "Ngày bắt đầu", width: "9%" },
              { key: "days", label: "Số ngày", width: "6%" },
              { key: "leaveType", label: "Loại nghỉ phép", width: "10%" },
              { key: "reason", label: "Lý do", align: "left", width: "12%" },
              {
                key: "status",
                label: "Trạng thái",
                width: "9%",
                render: (row) => (
                  <StatusChip label={row.ui.label} bg={row.ui.bg} color={row.ui.color} />
                ),
              },
              {
                key: "actions",
                label: "Thao tác",
                width: "8%",
                render: (row) =>
                  row.raw.status === REQUEST_STATUS.PENDING ? (
                    <Flex gap="8px" justify="center" onClick={(e) => e.stopPropagation()}>
                      <DesktopButton
                        h="32px"
                        p="0 10px"
                        fontSize="13px"
                        onClick={() => approve(row.raw, REQUEST_STATUS.APPROVED)}
                      >
                        Duyệt
                      </DesktopButton>
                      <DesktopButton
                        variant="danger"
                        h="32px"
                        p="0 10px"
                        fontSize="13px"
                        onClick={() => approve(row.raw, REQUEST_STATUS.REJECTED)}
                      >
                        Từ chối
                      </DesktopButton>
                    </Flex>
                  ) : (
                    "-"
                  ),
              },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {popovers}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Phê duyệt đơn xin nghỉ phép" />

      <Box px="16px" pt="12px">
        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard text="TỔNG SỐ ĐƠN" total={total} />

        <ListHeaderRow
          title="Danh sách đơn đơn xin nghỉ phép"
          sortLabel={sortOption.label}
          onSort={() => sheet.open("SORT")}
        />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText text="Không có đơn xin nghỉ phép" />
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it) => (
              <LeaveCard
                key={it._id}
                item={it}
                onClick={() => navigate(`${EMP_BASE_PATH}/quan-ly/phe-duyet-nghi-phep/${it._id}`)}
                onOpenMenu={(item, rect) => setMenu({ item, rect })}
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {popovers}
    </Box>
  );
};

export default LeaveApprovalList;
